import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
    Alert,
    Modal,
    Table
} from "react-bootstrap";
import {
    Link
} from "react-router-dom";

import ShopsContext from "../../../../context/ShopsContext";
import Loading from "../../../../components/Loading";
import DateFormatter from "../../../../components/DateFormatter";

function CourseLessonOpeningShopUsersModal({ show, handleClose, lesson }) {
    const shopsContext = useContext(ShopsContext);
    const [courseLessonOpeningShopUsers, setCourseLessonOpeningShopUsers] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if(!show || !lesson) {
            setCourseLessonOpeningShopUsers(null);
            setError(null);
            return () => {
                cancelled = true;
            };
        }
        const loadCourseLessonOpeningShopUsers = async () => {
            setCourseLessonOpeningShopUsers(null);
            setError(null);
            try {
                const response = await axios.post("/getCourseLessonOpeningShopUsers", {
                    shopId: shopsContext.currentShop.id,
                    courseLessonId: lesson.id
                });
                if(cancelled) {
                    return;
                }
                if(response.data.valid) {
                    setCourseLessonOpeningShopUsers(response.data.courseLessonOpeningShopUsers);
                } else {
                    setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
                }
            } catch(requestError) {
                if(cancelled) {
                    return;
                }
                console.error(requestError);
                setError("Er ging iets fout. Probeer het later opnieuw.");
            }
        };
        loadCourseLessonOpeningShopUsers();
        return () => {
            cancelled = true;
        };
    }, [lesson, show, shopsContext.currentShop.id]);

    const shopUserBaseUrl = useMemo(() => {
        return "/shop/" + shopsContext.currentShop.codeName + "/shop-user/";
    }, [shopsContext.currentShop.codeName]);
    const openedShopUsers = courseLessonOpeningShopUsers?.openedShopUsers || [];
    const notOpenedShopUsers = courseLessonOpeningShopUsers?.notOpenedShopUsers || [];

    return (
        <Modal show={ show } onHide={ handleClose } size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    Les openingen{ lesson?.title ? ": " + lesson.title : "" }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { error && (
                    <Alert variant="danger">{ error }</Alert>
                )}
                { !error && !courseLessonOpeningShopUsers && (
                    <Loading/>
                )}
                { courseLessonOpeningShopUsers && (
                    <React.Fragment>
                        <div className="card course-statistics-summary mb-4">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 col-lg mb-3 mb-lg-0">
                                        <div className="course-statistics-summary__item">
                                            <small className="text-muted d-block">Toegang gehad</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseLessonOpeningShopUsers.amountOfShopUsersThatEverHadAccess }
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="col-md-6 col-lg mb-3 mb-lg-0">
                                        <div className="course-statistics-summary__item">
                                            <small className="text-muted d-block">Gestart met cursus</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseLessonOpeningShopUsers.amountOfShopUsersThatStartedCourse }
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="col-md-6 col-lg mb-3 mb-lg-0">
                                        <div className="course-statistics-summary__item">
                                            <small className="text-muted d-block">Les geopend</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseLessonOpeningShopUsers.openedAmount }
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="col-md-6 col-lg mb-3 mb-lg-0">
                                        <div className={ "course-statistics-summary__item" + (courseLessonOpeningShopUsers.amountOfShopUsersThatNeverStartedCourse > 0 ? " course-statistics-summary__item--warning" : "") }>
                                            <small className="text-muted d-block">Nooit gestart</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseLessonOpeningShopUsers.amountOfShopUsersThatNeverStartedCourse }
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="col-md-6 col-lg">
                                        <div className="course-statistics-summary__item">
                                            <small className="text-muted d-block">Les niet geopend</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseLessonOpeningShopUsers.notOpenedAmount }
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-muted mb-0 mt-3">
                                    De lijsten hieronder tonen alleen gebruikers die al met de cursus gestart zijn.
                                    Gebruikers die nooit een les openden staan hier niet tussen.
                                </p>
                            </div>
                        </div>

                        { courseLessonOpeningShopUsers.amountOfShopUsersThatStartedCourse === 0 && (
                            <Alert variant="info">
                                Nog niemand is met deze cursus gestart, daarom zijn er geen gebruikers in deze lijsten.
                            </Alert>
                        )}

                        <h4>Gebruikers die de les openden</h4>
                        { openedShopUsers.length === 0 ? (
                            <p className="text-muted">
                                { courseLessonOpeningShopUsers.amountOfShopUsersThatStartedCourse === 0 ? (
                                    "Er zijn nog geen gestarte gebruikers."
                                ) : (
                                    "Nog niemand die met de cursus startte heeft deze les geopend."
                                ) }
                            </p>
                        ) : (
                            <Table size="sm" hover className="mb-4">
                                <thead className="thead-light">
                                <tr>
                                    <th className="text-center">#</th>
                                    <th className="text-left">Naam</th>
                                    <th className="text-left">Email</th>
                                    <th className="text-right">Eerste keer geopend</th>
                                    <th className="text-right">Laatst geopend</th>
                                    <th className="text-center">Afgerond</th>
                                    <th className="text-right">Afgerond op</th>
                                </tr>
                                </thead>
                                <tbody>
                                { openedShopUsers.map((shopUser) => {
                                    const url = shopUserBaseUrl + shopUser.id;
                                    return (
                                        <tr key={ shopUser.id } className="table-clickable-row">
                                            <th className="text-center">
                                                <Link to={ url }>
                                                    { shopUser.id }
                                                </Link>
                                            </th>
                                            <td className="text-left">
                                                <Link to={ url }>
                                                    { shopUser.name }
                                                </Link>
                                            </td>
                                            <td className="text-left">
                                                <Link to={ url }>
                                                    { shopUser.email }
                                                </Link>
                                            </td>
                                            <td className="text-right">
                                                <Link to={ url }>
                                                    { shopUser.courseLessonUserInfo?.firstViewedDate ? (
                                                        <DateFormatter date={ shopUser.courseLessonUserInfo.firstViewedDate }/>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </Link>
                                            </td>
                                            <td className="text-right">
                                                <Link to={ url }>
                                                    { shopUser.courseLessonUserInfo?.lastViewedDate ? (
                                                        <DateFormatter date={ shopUser.courseLessonUserInfo.lastViewedDate }/>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </Link>
                                            </td>
                                            <td className="text-center">
                                                <Link to={ url }>
                                                    { shopUser.courseLessonUserInfo?.completed ? (
                                                        <i className="fa-solid fa-check fa-fw text-success"/>
                                                    ) : (
                                                        <i className="fa-solid fa-times fa-fw text-danger"/>
                                                    )}
                                                </Link>
                                            </td>
                                            <td className="text-right">
                                                <Link to={ url }>
                                                    { shopUser.courseLessonUserInfo?.completedDate ? (
                                                        <DateFormatter date={ shopUser.courseLessonUserInfo.completedDate }/>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        )}

                        <h4>Gebruikers die met de cursus startten, maar deze les niet openden</h4>
                        { notOpenedShopUsers.length === 0 ? (
                            <p className="text-muted mb-0">
                                { courseLessonOpeningShopUsers.amountOfShopUsersThatStartedCourse === 0 ? (
                                    "Er zijn nog geen gestarte gebruikers."
                                ) : (
                                    "Iedereen die met de cursus startte heeft deze les geopend."
                                ) }
                            </p>
                        ) : (
                            <Table size="sm" hover className="mb-0">
                                <thead className="thead-light">
                                <tr>
                                    <th className="text-center">#</th>
                                    <th className="text-left">Naam</th>
                                    <th className="text-left">Email</th>
                                </tr>
                                </thead>
                                <tbody>
                                { notOpenedShopUsers.map((shopUser) => {
                                    const url = shopUserBaseUrl + shopUser.id;
                                    return (
                                        <tr key={ shopUser.id } className="table-clickable-row">
                                            <th className="text-center">
                                                <Link to={ url }>
                                                    { shopUser.id }
                                                </Link>
                                            </th>
                                            <td className="text-left">
                                                <Link to={ url }>
                                                    { shopUser.name }
                                                </Link>
                                            </td>
                                            <td className="text-left">
                                                <Link to={ url }>
                                                    { shopUser.email }
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        )}
                    </React.Fragment>
                )}
            </Modal.Body>
        </Modal>
    );
}
CourseLessonOpeningShopUsersModal.propTypes = {
    show: PropTypes.bool,
    handleClose: PropTypes.func.isRequired,
    lesson: PropTypes.object
}

export default React.memo(CourseLessonOpeningShopUsersModal);
